import React, { useState, useEffect, useRef } from 'react';
import * as Tone from 'tone';
import '../styles/MPCBeatPad.css';
    
const MPCBeatPad = ({ onPadHit }) => {
  const [activeButtons, setActiveButtons] = useState({});
  const playersRef = useRef({});
  const envelopesRef = useRef({});
  const [isLoaded, setIsLoaded] = useState(false);
  const [loadingProgress, setLoadingProgress] = useState(0);
  const activePlayersRef = useRef(new Set());

  // Define the pad layout with corresponding keys
  const padLayout = [
    [{ num: '0', key: '1' }, { num: '1', key: '2' }, { num: '2', key: '3' }, { num: '3', key: '4' }],
    [{ num: '4', key: 'q' }, { num: '5', key: 'w' }, { num: '6', key: 'e' }, { num: '7', key: 'r' }],
    [{ num: '8', key: 'a' }, { num: '9', key: 's' }, { num: '10', key: 'd' }, { num: '11', key: 'f' }],
    [{ num: '12', key: 'z' }, { num: '13', key: 'x' }, { num: '14', key: 'c' }, { num: '15', key: 'v' }]
  ];

  // Mapping of keyboard keys to pad indices
  const keyToPadMap = {
    '1': [0, 0], '2': [0, 1], '3': [0, 2], '4': [0, 3],
    'q': [1, 0], 'w': [1, 1], 'e': [1, 2], 'r': [1, 3],
    'a': [2, 0], 's': [2, 1], 'd': [2, 2], 'f': [2, 3],
    'z': [3, 0], 'x': [3, 1], 'c': [3, 2], 'v': [3, 3]
  };

  // Initialize Tone.js Players
  useEffect(() => {
    const initPlayers = async () => {
      try {
        await Tone.start();
        
        // Create a player for each of the 16 audio files
        const players = {};
        const envelopes = {};
        let loadedCount = 0;
        
        // Create promises for loading all 16 audio files
        const loadPromises = [];
        
        // Map each pad to its corresponding audio file
        for (let i = 0; i < 16; i++) {
          const padNumber = i + 1; // Files are named 1.mp3 through 16.mp3
          const playerPromise = new Promise((resolve) => {
            // Create a player with an envelope to smooth transitions
            const player = new Tone.Player({
              url: `/audio/chops2/${padNumber}.mp3`,
              loop: false, // Changed to false to prevent automatic looping
              autostart: false,
              onload: () => {
                loadedCount++;
                setLoadingProgress(Math.floor((loadedCount / 16) * 100));
                resolve();
              }
            });
            
            // Create an envelope for smooth release
            const envelope = new Tone.AmplitudeEnvelope({
              attack: 0.01,
              decay: 0.2,
              sustain: 0.8,
              release: 0.05, // Quick release for more responsive retriggering
            }).toDestination();
            
            // Connect player to envelope instead of directly to destination
            player.connect(envelope);
            
            players[i] = player;
            envelopes[i] = envelope;
          });
          
          loadPromises.push(playerPromise);
        }
        
        // Wait for all players to load
        await Promise.all(loadPromises);
        playersRef.current = players;
        envelopesRef.current = envelopes;
        setIsLoaded(true);
        
      } catch (error) {
        console.error('Error initializing MPC players:', error);
      }
    };
    
    initPlayers();
    
    return () => {
      // Clean up when component unmounts
      const players = playersRef.current;
      const envelopes = envelopesRef.current;
      if (players) {
        Object.values(players).forEach(player => {
          if (player) player.dispose();
        });
      }
      if (envelopes) {
        Object.values(envelopes).forEach(envelope => {
          if (envelope) envelope.dispose();
        });
      }
    };
  }, []);

  // Setup keyboard event listeners
  useEffect(() => {
    const handleKeyDown = (e) => {
      // Ignore if keys are pressed while typing in an input field or key is repeated
      if (e.target.tagName === 'INPUT' || e.target.tagName === 'TEXTAREA' || e.repeat) {
        return;
      }
      
      const key = e.key.toLowerCase();
      if (keyToPadMap[key] && isLoaded) {
        const [row, col] = keyToPadMap[key];
        startPad(row, col);
      }
    };
    
    const handleKeyUp = (e) => {
      const key = e.key.toLowerCase();
      if (keyToPadMap[key] && isLoaded) {
        const [row, col] = keyToPadMap[key];
        stopPad(row, col);
      }
    };
    
    window.addEventListener('keydown', handleKeyDown);
    window.addEventListener('keyup', handleKeyUp);
    
    return () => {
      window.removeEventListener('keydown', handleKeyDown);
      window.removeEventListener('keyup', handleKeyUp);
    };
  }, [isLoaded]);

  // Start playing a pad sound
  const startPad = (row, col) => {
    const padIndex = parseInt(padLayout[row][col].num);
    const padKey = `${row}-${col}`;
    
    if (playersRef.current && isLoaded && playersRef.current[padIndex]) {
      const player = playersRef.current[padIndex];
      const envelope = envelopesRef.current[padIndex];
      
      // Always stop the player first to reset its position to the beginning
      if (player.state === "started") {
        player.stop();
      }
      
      // Start the player from the beginning and trigger the envelope attack
      player.start();
      envelope.triggerAttack();

      if (onPadHit) onPadHit();
      
      // Add to active players
      activePlayersRef.current.add(padKey);
      
      // Visual feedback - highlight the pad while active
      setActiveButtons(prev => ({ ...prev, [padKey]: true }));
    }
  };
  
  // Stop playing a pad sound
  const stopPad = (row, col) => {
    const padIndex = parseInt(padLayout[row][col].num);
    const padKey = `${row}-${col}`;
    
    if (playersRef.current && playersRef.current[padIndex]) {
      const player = playersRef.current[padIndex];
      const envelope = envelopesRef.current[padIndex];
      
      // Trigger release on the envelope to fade out
      envelope.triggerRelease();
      
      // Schedule the actual player stop after the release time
      setTimeout(() => {
        // Only stop if still not activated again
        if (!activePlayersRef.current.has(padKey)) {
          player.stop();
        }
      }, envelopesRef.current[padIndex].release * 1000);
      
      // Remove from active players
      activePlayersRef.current.delete(padKey);
      
      // Remove visual feedback
      setActiveButtons(prev => ({ ...prev, [padKey]: false }));
    }
  };

  // Handle pad mouse events
  const handlePadMouseDown = (row, col) => {
    startPad(row, col);
  };
  
  const handlePadMouseUp = (row, col) => {
    stopPad(row, col);
  };
  
  const handlePadMouseLeave = (row, col) => {
    // Only stop if this pad is currently active
    if (activeButtons[`${row}-${col}`]) {
      stopPad(row, col);
    }
  };

  return (
    <div className="mpc-container">
      <div className="mpc-frame">
        <div className="mpc-branding">
          <div className="mpc-brand">AKAI</div>
          <div className="mpc-model">Professional</div>
        </div>
        
        <div className="mpc-lcd">
          <div className="lcd-screen">
            {!isLoaded 
              ? `LOADING ${loadingProgress}%` 
              : "BEAT CHOPPER"}
          </div>
        </div>
        
        <div className="mpc-pad-grid">
          {padLayout.map((row, rowIndex) => (
            <div key={rowIndex} className="mpc-pad-row">
              {row.map((pad, colIndex) => (
                <button
                  key={`${rowIndex}-${colIndex}`}
                  className={`mpc-pad ${activeButtons[`${rowIndex}-${colIndex}`] ? 'active' : ''}`}
                  onMouseDown={() => handlePadMouseDown(rowIndex, colIndex)}
                  onMouseUp={() => handlePadMouseUp(rowIndex, colIndex)}
                  onMouseLeave={() => handlePadMouseLeave(rowIndex, colIndex)}
                  onTouchStart={(e) => {
                    e.preventDefault();
                    handlePadMouseDown(rowIndex, colIndex);
                  }}
                  onTouchEnd={(e) => {
                    e.preventDefault();
                    handlePadMouseUp(rowIndex, colIndex);
                  }}
                  disabled={!isLoaded}
                >
                  <div className="pad-content">
                    <div className="pad-number">{parseInt(pad.num) + 1}</div>
                    <div className="pad-key">{pad.key}</div>
                  </div>
                </button>
              ))}
            </div>
          ))}
        </div>
        
        <div className="mpc-controls">
          <div className="control-section">
            {/* Placeholder for additional controls */}
          </div>
        </div>
      </div>
    </div>
  );
};

export default MPCBeatPad;